import Draggable from 'vuedraggable';

import { DropdownText } from '@/components/Common/Dropdown';

import DragHandleIcon from '@svgIcons/dragable-drag-handle.svg';
import CloseIcon from '@svgIcons/close.svg';
import CheckIcon from '@svgIcons/check.svg';
import DescendingIcon from '@svgIcons/dashboard/descending.svg';

import { isValueExist, changeOrder } from '@util';

import * as S from './styles';

const NONE_ID = '';

/**
 * ExposureDropdown (string|number 기반)
 *
 * exposureList, selectedValue, fixedList 모두 string|number 타입으로 받는다.
 * translator 는 (id: string|number) => string 형태.
 */
export default {
    name: 'QuickControlDropdown',
    props: {
        fieldType: String,
        /**
         * 선택된 항목 목록 (string|number[])
         */
        exposureList: {
            type: Array,
            default() {
                return [];
            },
        },
        /**
         * 현재 선택값 (string|number)
         */
        selectedValue: {
            type: [String, Number],
            default: '',
        },
        /**
         * id → 표시 텍스트 변환 함수
         */
        translator: {
            type: Function,
            default: (id) => id,
        },
        /**
         * 항상 고정 표시할 옵션 목록 (string|number[])
         */
        fixedList: {
            type: Array,
            default() {
                return [];
            },
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showOptions: false,
            isDragging: false,
        };
    },
    watch: {
        isDropdownEmpty(val) {
            if (val) this.showOptions = false;
        },
    },
    computed: {
        triggerText() {
            const { selectedValue, translator, fixedList } = this;
            if (selectedValue !== '' && selectedValue != null) {
                return translator(selectedValue);
            }
            // selectedValue === '' 이고 fixed에 None(='')이 있으면 "None" 표시
            const hasNoneFixed = fixedList.includes(NONE_ID);
            return hasNoneFixed ? translator(NONE_ID) : undefined;
        },
        allOptions() {
            const { fixedList, exposureList, selectedValue } = this;
            let editable = [...(exposureList ?? [])];

            // selectedValue 가 fixed/editable 어디에도 없으면 editable 맨 앞에 보여줌
            if (
                selectedValue !== '' &&
                selectedValue != null &&
                !editable.includes(selectedValue) &&
                !fixedList.includes(selectedValue)
            ) {
                editable = [selectedValue, ...editable];
            }

            return { fixed: fixedList, editable };
        },
        isDropdownEmpty() {
            const { fixed, editable } = this.allOptions;
            return !isValueExist([...fixed, ...editable]);
        },
    },
    methods: {
        handleDragEnd({ oldIndex, newIndex }) {
            const newList = changeOrder({
                oldIndex,
                newIndex,
                value: [...this.exposureList],
            });
            this.$emit('update:list', newList);
        },
        isSameAsSelected(id) {
            return String(id) === String(this.selectedValue);
        },
        handleRemoveItem(id) {
            this.$emit('remove:item', id);
        },
        handleSelectItem(id) {
            this.$emit('select:item', id);
            this.showOptions = false;
        },
        renderFixedRow(id) {
            const { translator } = this;
            const isSelected = this.isSameAsSelected(id);
            const label =
                id === NONE_ID
                    ? this.$t('NEW_DASHBOARD.graph_dropdown_default')
                    : translator(id);

            return (
                <S.Row
                    key={String(id)}
                    selected={isSelected}
                    onClick={(e) => {
                        e.stopPropagation();
                        this.handleSelectItem(id);
                    }}
                >
                    <S.Label>{label}</S.Label>
                    <CheckIcon v-view-box class="check-icon" />
                </S.Row>
            );
        },
        renderDraggableRow(id) {
            const { translator, isDragging } = this;
            const isSelected = this.isSameAsSelected(id);
            const label = translator(id);

            return (
                <S.Row key={String(id)} selected={isSelected}>
                    <S.DragHandle onClick={(e) => e.stopPropagation()}>
                        <DragHandleIcon v-view-box />
                    </S.DragHandle>

                    <S.ClickArea
                        onClick={(e) => {
                            e.stopPropagation();
                            this.handleSelectItem(id);
                        }}
                    >
                        {this.wrapTooltip({
                            visible: !isDragging,
                            title: label,
                            content: <S.Label>{label}</S.Label>,
                        })}
                        <CheckIcon v-view-box class="check-icon" />
                    </S.ClickArea>

                    <S.DeleteButton
                        class="no-drag"
                        onClick={(e) => {
                            e.stopPropagation();
                            this.handleRemoveItem(id);
                        }}
                    >
                        <CloseIcon v-view-box />
                    </S.DeleteButton>
                </S.Row>
            );
        },
        renderOverlay() {
            const { fixed, editable } = this.allOptions;

            if (this.isDropdownEmpty) {
                return <span />;
            }

            return (
                <S.OverlayContainer
                    slot="overlay"
                    onMousedown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    {fixed.map(this.renderFixedRow)}
                    <Draggable
                        value={[...editable]}
                        animation={200}
                        ghostClass="ghost"
                        forceFallback={true}
                        filter=".no-drag"
                        on={{
                            start: () => {
                                this.isDragging = true;
                            },
                            end: ({ oldIndex, newIndex }) => {
                                this.isDragging = false;
                                this.handleDragEnd({ oldIndex, newIndex });
                            },
                        }}
                    >
                        {editable.map(this.renderDraggableRow)}
                    </Draggable>
                </S.OverlayContainer>
            );
        },
    },
    render() {
        const { disabled, isDropdownEmpty, triggerText } = this;
        const isDisabled = disabled || isDropdownEmpty;

        return (
            <S.DropdownWrapper onClick={(e) => e.stopPropagation()}>
                <a-dropdown
                    trigger={['click']}
                    disabled={disabled}
                    placement="bottomLeft"
                    visible={this.showOptions}
                    getPopupContainer={(trigger) => trigger.parentElement}
                    on={{
                        visibleChange: (v) => {
                            if (v && isDisabled) return;
                            this.showOptions = v;
                        },
                    }}
                >
                    <S.DropdownTrigger
                        disabled={isDisabled}
                        class={isDisabled ? 'disabled' : ''}
                    >
                        <DropdownText
                            hasPlaceholder={triggerText == null}
                        >
                            {triggerText ??
                                this.$t(
                                    `FIELD_PANEL.${this.fieldType}_exposure_setting.dropdown_placeholder`
                                )}
                        </DropdownText>
                        {!isDisabled && (
                            <DescendingIcon v-view-box class="arrow-icon" />
                        )}
                    </S.DropdownTrigger>
                    {this.renderOverlay()}
                </a-dropdown>
            </S.DropdownWrapper>
        );
    },
};
